import { createContext, useCallback, useContext, useState } from 'react';

import { Trait } from '@/api';
import { EntityType } from '@/models/entity';
import { useLocalStorageState } from '@/services/localStorage';
import useDebouncedState from '@/utils/useDebouncedState';

type FiltersState = {
  page: {
    page: number;
    setPage: (page: number) => void;
  };
  search: {
    search: string;
    debouncedSearch: string;
    debouncedSetSearch: (search: string) => void;
  };
  traits: {
    traits: Trait[];
    setTraits: (traits: Trait[]) => void;
  };
  types: {
    types: EntityType[];
    setTypes: (types: EntityType[]) => void;
  };
  groups: {
    groups: { id: string; name: string }[];
    setGroups: (groups: { id: string; name: string }[]) => void;
  };
  activeFiltersCount: number;
  clearFilters: () => void;
};

export const FiltersStateContext = createContext<FiltersState>({} as FiltersState);

export const useFiltersState = (): FiltersState => {
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), [setPage]);

  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const [traits, setTraits] = useLocalStorageState<Trait[]>(
    'settings:organization:entities:entities:filters:traits',
    []
  );

  const [types, setTypes] = useLocalStorageState<EntityType[]>(
    'settings:organization:entities:entities:filters:types',
    []
  );

  const [groups, setGroups] = useLocalStorageState<{ id: string; name: string }[]>(
    'settings:organization:entities:entities:filters:groups',
    []
  );

  return {
    page: {
      page,
      setPage,
    },
    search: {
      search,
      debouncedSearch,
      debouncedSetSearch,
    },
    traits: {
      traits,
      setTraits: (traits: Trait[]) => {
        setTraits(traits);
        resetPage();
      },
    },
    types: {
      types,
      setTypes: (types: EntityType[]) => {
        setTypes(types);
        resetPage();
      },
    },
    groups: {
      groups,
      setGroups: (groups: { id: string; name: string }[]) => {
        setGroups(groups);
        resetPage();
      },
    },
    activeFiltersCount: traits.length + types.length + groups.length,
    clearFilters: () => {
      setTraits([]);
      setTypes([]);
      setGroups([]);
      resetPage();
    },
  };
};

export const useFilters = () => {
  return useContext(FiltersStateContext);
};
